/**
 * AgentFilter Component
 *
 * Displays agents as clickable pill/label buttons for filtering.
 * Shows event count per agent and highlights active filter.
 */

import { Component, For, Show } from 'solid-js';
import type { AgentInfo } from '../../stores/executionStore.ts';

// Colors for different agents - cycle through these
const AGENT_COLORS = [
  { bg: 'bg-purple-500/20', border: 'border-purple-500/40', text: 'text-purple-300', dot: 'bg-purple-400' },
  { bg: 'bg-blue-500/20', border: 'border-blue-500/40', text: 'text-blue-300', dot: 'bg-blue-400' },
  { bg: 'bg-cyan-500/20', border: 'border-cyan-500/40', text: 'text-cyan-300', dot: 'bg-cyan-400' },
  { bg: 'bg-green-500/20', border: 'border-green-500/40', text: 'text-green-300', dot: 'bg-green-400' },
  { bg: 'bg-yellow-500/20', border: 'border-yellow-500/40', text: 'text-yellow-300', dot: 'bg-yellow-400' },
  { bg: 'bg-orange-500/20', border: 'border-orange-500/40', text: 'text-orange-300', dot: 'bg-orange-400' },
  { bg: 'bg-pink-500/20', border: 'border-pink-500/40', text: 'text-pink-300', dot: 'bg-pink-400' },
];

export function getAgentColor(index: number) {
  return AGENT_COLORS[index % AGENT_COLORS.length];
}

export function getAgentColorById(agentId: string, agents: AgentInfo[]) {
  const index = agents.findIndex(a => a.id === agentId);
  return index >= 0 ? getAgentColor(index) : AGENT_COLORS[0];
}

interface AgentFilterProps {
  agents: AgentInfo[];
  selectedAgentId: string | null;
  onSelect: (agentId: string | null) => void;
  totalEventCount?: number;
}

const AgentFilter: Component<AgentFilterProps> = (props) => {
  const totalEvents = () => {
    if (props.totalEventCount !== undefined) return props.totalEventCount;
    return props.agents.reduce((sum, a) => sum + a.eventCount, 0);
  };

  return (
    <div class="flex flex-wrap items-center gap-2">
      {/* All agents button */}
      <button
        type="button"
        onClick={() => props.onSelect(null)}
        class={`
          inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium
          border transition-all duration-150
          ${props.selectedAgentId === null
            ? 'bg-white/10 border-white/30 text-white'
            : 'bg-gray-800/50 border-gray-700 text-gray-400 hover:bg-gray-800 hover:text-gray-300'
          }
        `}
      >
        <span class="w-2 h-2 rounded-full bg-gray-400" />
        <span>All</span>
        <span class="text-xs opacity-70">({totalEvents()})</span>
      </button>

      {/* Individual agent buttons */}
      <For each={props.agents}>
        {(agent, index) => {
          const color = getAgentColor(index());
          const isSelected = () => props.selectedAgentId === agent.id;

          return (
            <button
              type="button"
              onClick={() => props.onSelect(isSelected() ? null : agent.id)}
              class={`
                inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium
                border transition-all duration-150
                ${isSelected()
                  ? `${color.bg} ${color.border} ${color.text}`
                  : `bg-gray-800/50 border-gray-700 text-gray-400 hover:${color.bg} hover:${color.border} hover:${color.text}`
                }
              `}
            >
              <span class={`w-2 h-2 rounded-full ${color.dot}`} />
              <span class="truncate max-w-[150px]">{agent.name || agent.id.slice(0, 8)}</span>
              <span class="text-xs opacity-70">({agent.eventCount})</span>
            </button>
          );
        }}
      </For>
    </div>
  );
};

export default AgentFilter;
